import { uiLocale } from '@/i18n'

// Shared dd.mm.yyyy formatter (UI locale). Consolidates the copies that lived in
// admin tables and the MCP connections row.
export const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(uiLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })

/**
 * Дата из контракта — строка, и она может приехать битой (усечённой, с чужим
 * форматом, `null` в нестрогом ответе). `Intl.DateTimeFormat.format` на
 * `Invalid Date` БРОСАЕТ `RangeError`, а не печатает мусор, — то есть одна
 * кривая строка в ответе уносит весь список, а вне редактора (единственный
 * `ErrorBoundary` стоит внутри него) и всё приложение.
 *
 * Прочерк вместо даты честнее: строка остаётся читаемой, а сумма и статус —
 * на месте. `formatDate` выше этой защиты не имеет намеренно: он печатает
 * даты, которые бэк отдаёт обязательными полями, и его вызывающие не
 * пересматривались.
 */
export const formatDateOrDash = (
  value: string | null | undefined,
  fmt: Intl.DateTimeFormat,
): string => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : fmt.format(date)
}

/**
 * «5 окт., 2026, 10:15» (en: «Oct 5, 2026, 10:15»): дата, год и время одной
 * строкой для списков истории. Собирается из частей `formatToParts`, потому
 * что штатный русский вывод — «5 окт. 2026 г., 10:15» с хвостом «г.». Битая
 * строка — прочерк, как у `formatDateOrDash`.
 */
export const formatDayYearTime = (value: string | null | undefined): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  const locale = uiLocale()
  const part = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, options).format(date)
  const time = part({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
  return `${part({ day: 'numeric', month: 'short' })}, ${part({ year: 'numeric' })}, ${time}`
}
